/**
 * Drives a browser download and owns its live state.
 *
 * A module-level store rather than component state: the progress panel lives inside the
 * download cart, which re-renders whenever the cart is refreshed, and a download that reset
 * itself on every re-render would be useless. Same pattern as `features/incomingReview.ts`.
 *
 * Only one download runs at a time. The server refuses a second job while one is IN_PROGRESS
 * (a CONFLICT from `startBrowserDownload`); this guard just avoids making the round trip to
 * be told so. Until 2026-09-03 the server did NOT enforce it and this comment was wrong —
 * a second start orphaned the first job permanently.
 */
import { api } from "../../api";
import { toastError, toastSuccess } from "../../ui/messages";
import {
  rebuildDownloadPart,
  runBrowserDownload,
  type ChunkProgress,
  type DownloadPart,
} from "./engine";

export interface DownloadState {
  running: boolean;
  /** Whole-job progress, in artworks. */
  artworksDone: number;
  artworksTotal: number;
  /** Progress within the chunk currently being fetched, in files. */
  filesDone: number;
  filesTotal: number;
  chunkIndex: number;
  chunkCount: number;
  /** Files that could not be fetched after retries. */
  failures: string[];
  /** Set when a previous job was left unfinished and can be picked up. */
  resumable: {
    downloadId: string;
    /** Part layout planned by the server, byte-aware. Not recomputed here: the browser has
     *  no idea how large each original is, and guessing produced multi-GB zips. Finished
     *  parts are already filtered out and the rest keep their original numbering. */
    parts: DownloadPart[];
    completed: number;
    total: number;
  } | null;
  /** Set while ONE already-delivered zip is being rebuilt from the downloads modal. */
  rebuilding: { downloadId: string; partIndex: number; filesDone: number; filesTotal: number } | null;
  error: string | null;
}

const EMPTY: DownloadState = {
  running: false,
  artworksDone: 0,
  artworksTotal: 0,
  filesDone: 0,
  filesTotal: 0,
  chunkIndex: 0,
  chunkCount: 0,
  failures: [],
  resumable: null,
  rebuilding: null,
  error: null,
};

let state: DownloadState = { ...EMPTY };
let abort: AbortController | null = null;
const listeners = new Set<(s: DownloadState) => void>();

export function getDownloadState(): DownloadState {
  return state;
}

export function onDownloadStateChange(fn: (s: DownloadState) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function set(patch: Partial<DownloadState>): void {
  state = { ...state, ...patch };
  for (const fn of listeners) fn(state);
}

function applyProgress(p: ChunkProgress): void {
  set({
    filesDone: p.filesDone,
    filesTotal: p.filesTotal,
    artworksDone: p.artworksDone,
    artworksTotal: p.artworksTotal,
    chunkIndex: p.chunkIndex,
    chunkCount: p.chunkCount,
    failures: p.failures,
  });
}

async function drive(
  downloadId: string,
  parts: DownloadPart[],
  artworksTotal: number,
  alreadyDone: number
): Promise<void> {
  abort = new AbortController();
  set({
    running: true,
    error: null,
    failures: [],
    artworksDone: alreadyDone,
    artworksTotal,
    chunkCount: parts.length,
    resumable: null,
  });

  await runBrowserDownload(
    { downloadId, parts, artworksTotal, artworksAlreadyDone: alreadyDone, signal: abort.signal },
    {
      onProgress: applyProgress,
      onDone: ({ artworksDone, failures }) => {
        set({ running: false, artworksDone, failures });
        if (artworksDone >= artworksTotal) {
          toastSuccess(`Download complete — ${artworksDone} artworks.`);
        } else {
          // Not an error: an aborted or partly-failed job can be picked up later, and the
          // server still holds the job with what was delivered.
          toastSuccess(`Stopped at ${artworksDone} of ${artworksTotal}. You can resume later.`);
        }
        void refreshResumable();
      },
      onError: (message) => {
        set({ running: false, error: message });
        toastError(message);
      },
    }
  );
}

/** Start a new browser download for the given artwork ids. */
export async function startBrowserDownload(artworkIds: number[]): Promise<void> {
  if (state.running) return;
  try {
    const plan = await api.downloadCart.startBrowserDownload({ id: artworkIds });
    if (plan.blockedCount > 0) {
      toastError(`${plan.blockedCount} artwork(s) are not available to you and were removed.`);
    }
    await drive(plan.downloadId, plan.parts, plan.artworkIds.length, 0);
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Could not start the download";
    set({ running: false, error: message });
    toastError(message);
    // The likeliest refusal is "you already have an unfinished download", so re-check for one
    // — that turns a dead-end error into the Resume/Cancel prompt that resolves it.
    void refreshResumable();
  }
}

/** Pick up an unfinished job, fetching only what is missing. */
export async function resumeBrowserDownload(): Promise<void> {
  if (state.running || !state.resumable) return;
  const { downloadId, parts, completed, total } = state.resumable;
  await drive(downloadId, parts, total, completed);
}

/**
 * Stop after the file currently in flight.
 *
 * Chunks already handed to the browser stay valid and stay reported — abort loses at most the
 * chunk being assembled, which is the whole reason the work is chunked.
 */
export function abortBrowserDownload(): void {
  abort?.abort();
}

/**
 * Abandon an unfinished job so it stops being offered.
 *
 * Artworks already delivered keep their licences — they were licensed when the files landed,
 * and declining the remainder does not undo that.
 */
export async function cancelResumable(): Promise<void> {
  if (state.running || !state.resumable) return;
  const { downloadId } = state.resumable;
  try {
    await api.downloadCart.cancelBrowserDownload({ downloadId });
    set({ resumable: null });
    toastSuccess("Unfinished download discarded.");
  } catch (err: unknown) {
    toastError(err instanceof Error ? err.message : "Could not cancel the download");
  }
}

/** Ask the server whether this member has an unfinished job to offer. */
export async function refreshResumable(): Promise<void> {
  try {
    const r = await api.downloadCart.browserDownloadResume();
    set({
      resumable:
        r.state === "resumable"
          ? {
              downloadId: r.downloadId,
              parts: r.parts,
              completed: r.completed,
              total: r.total,
            }
          : null,
    });
  } catch {
    // A failed check must not break the cart; the reseller can still start a fresh download.
    set({ resumable: null });
  }
}

/**
 * Re-deliver ONE zip from an earlier download, by its part number.
 *
 * Separate from `running`: rebuilding a part of an old download is not the same activity as
 * a job in progress, and sharing the flag would make the downloads modal claim a download is
 * running (offering Abort, blocking Start) when nothing of the sort is happening.
 *
 * It deliberately refuses while a job IS running. The two would compete for the same fetch
 * concurrency and, worse, the browser hands zips over by clicking an anchor — two archives
 * arriving at once is exactly the confusion this feature exists to resolve.
 */
export async function rebuildDownloadPartById(
  downloadId: string,
  partIndex: number
): Promise<void> {
  if (state.running || state.rebuilding) return;
  set({ rebuilding: { downloadId, partIndex, filesDone: 0, filesTotal: 0 } });
  const controller = new AbortController();
  try {
    const plan = await api.downloadCart.rebuildBrowserPart({ downloadId, partIndex });
    if (plan.files.length === 0) {
      toastError("None of the artworks in this part are available to you any more.");
      set({ rebuilding: null });
      return;
    }
    if (plan.missing > 0) {
      // Said plainly rather than silently shipping a short zip: the reseller is replacing a
      // file they already have, so a smaller one needs explaining.
      toastError(
        `${plan.missing} artwork(s) in this part are no longer available to you and are not included.`
      );
    }
    await rebuildDownloadPart(
      { files: plan.files, filename: plan.filename, signal: controller.signal },
      {
        onProgress: (filesDone, filesTotal) =>
          set({ rebuilding: { downloadId, partIndex, filesDone, filesTotal } }),
        onDone: (failures) => {
          set({ rebuilding: null });
          if (failures.length > 0) {
            toastError(`Part ${String(partIndex)} rebuilt with ${String(failures.length)} missing file(s).`);
          } else {
            toastSuccess(`Part ${String(partIndex)} downloaded again.`);
          }
        },
      }
    );
  } catch (err: unknown) {
    set({ rebuilding: null });
    toastError(err instanceof Error ? err.message : "Could not rebuild this part");
  }
}
